import { newRelic } from '~/utils/newRelic/newRelic';
import { observability } from '~/utils/observability';

const MESSENGER_STORAGE_PREFIX = 'ZD-';
const BOUND_USER_STORAGE_KEY = 'shippo-zendesk-bound-user';
const LOGOUT_TIMEOUT_MS = 1_500;

const readBoundUser = () => localStorage.getItem(BOUND_USER_STORAGE_KEY);
const writeBoundUser = (userId: string) => localStorage.setItem(BOUND_USER_STORAGE_KEY, userId);
const clearBoundUser = () => localStorage.removeItem(BOUND_USER_STORAGE_KEY);
const hasMessengerStorage = () =>
  Object.keys(localStorage).some((key) => key.startsWith(MESSENGER_STORAGE_PREFIX));
const clearMessengerStorage = () => {
  Object.keys(localStorage)
    .filter((key) => key.startsWith(MESSENGER_STORAGE_PREFIX))
    .forEach((key) => localStorage.removeItem(key));

  localStorage.removeItem(BOUND_USER_STORAGE_KEY);
};

let pendingTransition: Promise<void> = Promise.resolve();

const serialize = (transition: () => Promise<void>): Promise<void> => {
  const next = pendingTransition.then(transition, transition);

  pendingTransition = next.catch(() => undefined);

  return next;
};

const loginUser = (jwt: string, userId: string) => {
  if (!window.zE) return;

  writeBoundUser(userId);
  observability.startEvent('ZENDESK_LOGIN', {
    id: userId,
    preservedAttributes: { userObjectId: userId },
  });

  window.zE(
    'messenger',
    'loginUser',
    (callback: (jwt: string) => void) => callback(jwt),
    (error: Maybe<{ message: string; reason: string; type: string }>) => {
      if (!error) {
        observability.successEvent('ZENDESK_LOGIN', { id: userId });
        return;
      }

      clearBoundUser();
      observability.errorEvent('ZENDESK_LOGIN', { ...error, id: userId });
      newRelic.noticeError('[Zendesk] Failed to authenticate', { ...error });
    },
  );
};

const tearDownMessenger = (): Promise<void> => {
  if (!window.zE) {
    clearMessengerStorage();

    return Promise.resolve();
  }

  observability.startEvent('ZENDESK_LOGOUT', {
    preservedAttributes: { userObjectId: readBoundUser() },
    timeoutMs: LOGOUT_TIMEOUT_MS,
  });

  return new Promise<void>((resolve) => {
    let timeoutId: Maybe<ReturnType<typeof setTimeout>> = null;
    let isSettled = false;

    const settle = (isConfirmed: boolean) => {
      if (isSettled) return;

      isSettled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (isConfirmed) observability.successEvent('ZENDESK_LOGOUT');

      try {
        clearMessengerStorage();
      } catch (error) {
        newRelic.noticeError('[Zendesk] Failed to clear the messenger storage', { error });
      } finally {
        resolve();
      }
    };

    timeoutId = setTimeout(() => settle(false), LOGOUT_TIMEOUT_MS);

    try {
      window.zE('messenger', 'logoutUser');
      window.zE('messenger', 'resetWidget', () => settle(true));
    } catch (error) {
      observability.errorEvent('ZENDESK_LOGOUT', { errorMessage: String(error) });
      newRelic.noticeError('[Zendesk] Failed to log the user out', { error });
      settle(false);
    }
  });
};

const unbindUser = (): Promise<void> => {
  if (!readBoundUser()) return Promise.resolve();

  return tearDownMessenger();
};

const syncUser = async (jwt: string, userId: string) => {
  const boundUser = readBoundUser();
  const isBoundToAnotherUser = boundUser ? boundUser !== userId : hasMessengerStorage();

  if (isBoundToAnotherUser) await tearDownMessenger();

  loginUser(jwt, userId);
};

export const zendeskLoginUser = (jwt: string, userId: string): Promise<void> =>
  serialize(async () => loginUser(jwt, userId));

export const zendeskLogoutUser = (): Promise<void> => serialize(tearDownMessenger);

export const zendeskUnbindUser = (): Promise<void> => serialize(unbindUser);

export const zendeskSyncUser = (jwt: string, userId: string): Promise<void> =>
  serialize(() => syncUser(jwt, userId));
